/**
 * The appointments screen's vocabulary — nothing but translation.
 *
 * Every string and class here maps a value the API already sent onto the words §A3 and the
 * state table in Part C use. No status is invented and none is collapsed: `ai_drafted`,
 * `td_modified`, `tms_pushed`, `error` and `overridden` are the five the store holds, so
 * they are the five the screen can print.
 */

import type { AppointmentWarning, OfficialWorkload, TechnicalSlot } from "@hockeytd/shared";

import type {
  AppointmentFixtureBody,
  AppointmentSlotBody,
  AppointmentStatus,
  OfficialView,
} from "../../api/types.js";
import {
  TOURNAMENT_TIMEZONE,
  tournamentClockParts,
  tournamentDateLong,
  tournamentTimeOfDay,
} from "../../app/tournamentTime.js";

/** §A3's status pill values, verbatim. */
const STATUS_LABEL: Readonly<Record<AppointmentStatus, string>> = {
  ai_drafted: "AI Drafted",
  td_modified: "TD Modified",
  tms_pushed: "TMS Pushed",
  error: "Error",
  overridden: "Overridden",
};

/** The pill variants the ported stylesheet already carries. */
const STATUS_PILL: Readonly<Record<AppointmentStatus, string>> = {
  ai_drafted: "statuspill",
  td_modified: "statuspill statuspill--warn",
  tms_pushed: "statuspill statuspill--good",
  error: "statuspill statuspill--alert",
  overridden: "statuspill statuspill--warn",
};

export function statusLabel(status: AppointmentStatus | null): string {
  return status === null ? "Unfilled" : STATUS_LABEL[status];
}

export function statusPillClass(status: AppointmentStatus | null): string {
  return status === null ? "statuspill statuspill--muted" : STATUS_PILL[status];
}

/** §A3's TMS Status column: blank is `—`, not an empty cell that reads as missing data. */
export function tmsStatusLabel(slot: AppointmentSlotBody): string {
  switch (slot.tmsStatus) {
    case "published":
      return "Published";
    case "draft":
      return "Draft";
    default:
      return "—";
  }
}

/**
 * RUN 82 — what a locked slot says, wherever it is refused (§A11).
 *
 * One constant rather than a sentence at each call site, because two functions state it —
 * `slotLock` for the name and `withdrawLock` for the withdrawal — and a TD who reads two
 * different reasons for one lock has been told that there are two locks. It names the way out,
 * which is the one thing that sets this lock apart from the other two: an Official match and a
 * signed-off slot have no press on this screen that reopens them, and a locked match does.
 */
export const LOCKED_ON_TMS_REASON = "Locked — On TMS. Unlock the match to correct it.";

/**
 * RUN 82 — why a slot reopened for a correction offers no Withdraw (§A11): the correction is
 * owed to TMS as well as to this screen, and the Needs Re-Push marker lives on the slot's row.
 *
 * RUN 82A — said ONCE per match on the screen, inside {@link NEEDS_REPUSH_NOTE}, and kept on each
 * slot for a screen reader only. Printed under TO, SJ and TJ it was the same 11px sentence three
 * times down one row.
 */
export const REOPENED_WITHDRAW_REASON =
  "Reopened for a correction — choose the correct official rather than withdrawing.";

/**
 * RUN 82A — what the `Needs Re-Push` chip says on its tooltip, which is also its accessible
 * description (§A11).
 *
 * Two things, in the order the TD needs them. First the slot-level refusal every reopened slot
 * carries, in {@link REOPENED_WITHDRAW_REASON}'s own words so the chip and the slots cannot tell
 * two different stories. Then what is owed outside this screen — and that sentence is careful not
 * to say what TMS holds. The screen cannot see TMS: the one fact it has is that it has not written
 * there, so that is the fact it states, followed by the way back to a locked day.
 */
export const NEEDS_REPUSH_NOTE =
  `${REOPENED_WITHDRAW_REASON} This screen does not change TMS — correct the crew there, then ` +
  "Confirm Day and Mark Day Locked again.";

/**
 * RUN 82D (QA pass 2) — what the `Needs Re-Push` chip says instead, on a match reopened for a
 * correction and then marked Official in TMS before it was confirmed again (§A11a).
 *
 * {@link NEEDS_REPUSH_NOTE} sends the TD to `Confirm Day` and `Mark Day Locked`, and on this match
 * neither can work: `Confirm Day` confirms around an Official match, and the lock skips an Official
 * match whose crew is not confirmed. So the note stops giving directions and says what the match
 * now is — owed, never to be settled here, and not counted as who officiated. It does not open with
 * the Withdraw sentence either: an Official match offers no picker, so "choose the correct official"
 * would be one more press that is not there.
 */
export const NEEDS_REPUSH_OFFICIAL_NOTE =
  "Reopened for a correction, then marked Official in TMS before it was confirmed again: it can " +
  "no longer be confirmed or locked here, so this crew is not counted as who officiated.";

/**
 * RUN 82G (QA pass 3) — what the chip says on a match reopened, confirmed again, and THEN marked
 * Official in TMS (§A11a).
 *
 * {@link NEEDS_REPUSH_NOTE} was wrong about this match twice: it said to choose the correct
 * official, and an Official match's slots are read-only; and it said to Confirm Day, which confirms
 * around an Official match and has nothing left to confirm on this one anyway. What still settles it
 * is the lock — a confirmed Official match is locked like any other — so that is the one press named.
 */
export const NEEDS_REPUSH_CONFIRMED_OFFICIAL_NOTE =
  "Reopened for a correction and confirmed again, then marked Official in TMS: its crew can no " +
  "longer change here. This screen does not change TMS — check the crew there, then Mark Day " +
  "Locked again.";

/**
 * RUN 82D — which of the notes a reopened match's chip carries.
 *
 * Only the match that went Official UNCONFIRMED gets the second: it is the one the lock will never
 * take. RUN 82G — a reopened match confirmed again before TMS marked it Official is still locked by
 * `Mark Day Locked`, and gets the third, which names that press and no other. A reopened match TMS
 * has not marked Official keeps the first.
 */
export function needsRePushNote(fixture: AppointmentFixtureBody): string {
  if (!fixture.frozen || !fixture.needsPush) return NEEDS_REPUSH_NOTE;
  return fixture.confirmed ? NEEDS_REPUSH_CONFIRMED_OFFICIAL_NOTE : NEEDS_REPUSH_OFFICIAL_NOTE;
}

/**
 * Whether the TD can still change this slot, and if so **which write** the change is.
 *
 * Three reasons a slot is read-only, and the screen says which: the fixture is Official in TMS
 * (§A4 rule 6), the slot is locked as the record TMS holds (§A11, RUN 82), or the slot has been
 * signed off (CC-Q25). None of them draws a control the API would refuse.
 *
 * **The order matters, and it follows what the TD can do about each.** Official comes first
 * because nothing reopens an Official match — telling him to unlock one would send him to a
 * press the API refuses (`appointments_frozen`). The lock comes before the sign-off because every
 * locked slot IS signed off (migration 059 will not lock one that is not), so a sign-off check
 * that ran first would answer every locked slot with `Confirmed — Signed Off` and hide the one
 * sentence that says how to correct it.
 *
 * Everything else is editable, but by one of two different routes, which is what `creates`
 * distinguishes. A filled slot has an appointment row and is edited by `PATCH
 * /appointments/:id`. An **empty** slot is the *absence* of a row — `id` is null, so there is
 * nothing to PATCH — and is filled by `POST /appointments`, addressed by fixture and slot
 * instead. §A6 gives the TD a dropdown on every slot; before that route existed this
 * function had to answer "not editable" on an empty slot because the front end had no way to
 * write one, which is why the copy below stayed on screen where the picker should have been.
 *
 * `reason` therefore survives on an empty slot as a *statement of fact* shown beside the
 * open picker, not in place of it: until an official is chosen the slot genuinely has not
 * been drafted, and the row should keep saying so. It falls silent the moment a name is on
 * the row — including the optimistic moment before the server has answered.
 *
 * A **confirmed fixture** needs no case of its own. `confirmed` means all three slots are
 * signed off (the API computes it that way), and a signed-off slot is a filled slot, so an
 * empty slot on a confirmed fixture is not a state that exists. The per-slot `confirmedAt`
 * check above is the whole of it.
 *
 * RUN 82D (QA pass 2) — the Official reason read `Appointments Locked — Match Is Official` until
 * §A11 gave `Locked` a meaning of its own: the record `Mark Day Locked` makes. An Official match is
 * not that, and one TMS marked Official unconfirmed is precisely a match the lock skips, so the
 * reason now names what the match is and what follows from it, in the words its chip uses.
 */
export function slotLock(
  fixture: AppointmentFixtureBody,
  slot: AppointmentSlotBody,
): { editable: boolean; creates: boolean; reason: string | null } {
  if (fixture.frozen) {
    return {
      editable: false,
      creates: false,
      reason: "Official in TMS — Appointments Can No Longer Change",
    };
  }
  if (slot.lockedAt !== null) {
    return { editable: false, creates: false, reason: LOCKED_ON_TMS_REASON };
  }
  if (slot.confirmedAt !== null) {
    return { editable: false, creates: false, reason: "Confirmed — Signed Off" };
  }
  if (slot.id === null) {
    return {
      editable: true,
      creates: true,
      reason: slot.mediaName === null ? "Not Drafted Yet" : null,
    };
  }
  return { editable: true, creates: false, reason: null };
}

/**
 * Whether the TD can **withdraw** this slot — which is a narrower question than whether he
 * can change it, and the two must not be collapsed.
 *
 * Everything that locks the slot for editing locks it for withdrawal too, and then two
 * states lock withdrawal alone, both of which `DELETE /appointments/:id` refuses with
 * `appointment_held`:
 *
 *  * **`overridden`** — the row carries a §A7 country-conflict override, which is a recorded
 *    decision. Deleting the row would take that record with it, so the recovery path is to
 *    reassign the slot to a clean official; the picker therefore stays open on an overridden
 *    slot and only this control closes.
 *  * **`tms_pushed`** — the appointment is in TMS. TMS is the record from that point, and
 *    withdrawing our row would leave the two disagreeing about a match that is already
 *    published.
 *
 * A control the API would refuse is worse than no control, and a control that vanishes
 * without a word reads as a bug — so each returns the copy that says which it is. `reason`
 * is null for the Official and sign-off locks, because `slotLock` has already said those out
 * loud in the Official column and the same sentence twice in one row is noise.
 *
 * **The §A11 lock is refused with its reason (RUN 82).** `DELETE /appointments/:id` answers a
 * locked slot with `appointment_held` and a message that it *is locked*, and the lock is the
 * one inherited state with a way out — `Unlock` on the row — so whoever asks this function why
 * a locked slot cannot be withdrawn is told what reopens it. The sentence is
 * `LOCKED_ON_TMS_REASON`, the very one `slotLock` puts on the name, which is what lets the
 * Fixture List go on printing it once: a refusal it has already said on the name is not said
 * again under the withdrawal. An Official match keeps its null, because nothing reopens it.
 */
export function withdrawLock(
  fixture: AppointmentFixtureBody,
  slot: AppointmentSlotBody,
): { withdrawable: boolean; reason: string | null } {
  if (!fixture.frozen && slot.lockedAt !== null) {
    return { withdrawable: false, reason: LOCKED_ON_TMS_REASON };
  }
  // RUN 82 (§A11) — a reopened slot is corrected by reassigning it: its row carries the
  // Needs Re-Push marker, and `DELETE` refuses it (`appointment_held`) rather than lose that.
  if (!fixture.frozen && slot.needsPush) {
    return { withdrawable: false, reason: REOPENED_WITHDRAW_REASON };
  }
  if (!slotLock(fixture, slot).editable) return { withdrawable: false, reason: null };
  // An empty slot is editable now that `POST /appointments` can fill one, but there is still
  // no row to withdraw — and "nothing to take away" is not a lock worth a sentence.
  if (slot.id === null) return { withdrawable: false, reason: null };
  if (slot.status === "overridden") {
    return {
      withdrawable: false,
      reason:
        "Cannot Be Withdrawn — reassign this slot to an official with no country conflict " +
        "first. Withdrawing it outright would erase the override's audit record.",
    };
  }
  if (slot.status === "tms_pushed") {
    return {
      withdrawable: false,
      reason:
        "Cannot Be Withdrawn — this appointment is already in TMS, which holds the record " +
        "from here.",
    };
  }
  return { withdrawable: true, reason: null };
}

/**
 * Whether this slot's appointment breaks a rule, and therefore earns the alert colour and
 * the "Rule Breach" marker beside the name.
 *
 * Two sources, and both count. `countryConflict` is the API's own answer to §A4 rule 2 — the
 * appointee's country is one of the two playing — and it stays true after a §A7 override,
 * which is the point of an override that is recorded rather than hidden. `ruleViolation` is
 * the named rule the row fell foul of, which on an `own_country` row says the same thing
 * twice but on a `two_roles_one_match` row is the only signal there is.
 *
 * A slot with nobody in it cannot breach anything, so an empty row is never marked.
 */
export function hasRuleBreach(slot: AppointmentSlotBody): boolean {
  if (slot.mediaName === null) return false;
  return slot.countryConflict || slot.ruleViolation !== null;
}

/**
 * The sentence behind the marker's tooltip — which rule, in the TD's own words.
 *
 * The marker itself is two words, because a table cell has room for two words. The reason
 * is what the `title` carries, and it must name the *specific* rule rather than repeat the
 * label: "rule breach — rule breach" tells a reader nothing he did not already see.
 *
 * `two_roles_one_match` is checked first: when a row somehow carries both, the double
 * appointment is the impossible one — it has no override at all — so it is the one worth
 * saying.
 */
export function ruleBreachReason(slot: AppointmentSlotBody): string {
  if (slot.ruleViolation === "two_roles_one_match") {
    return "Rule breach — this official already holds another slot in this match. One official, one role per match.";
  }
  if (slot.countryConflict || slot.ruleViolation === "own_country") {
    return "Rule breach — this official's own country is playing in this match, which is a hard-line rule.";
  }
  return "Rule breach — this appointment does not satisfy the appointment rules.";
}

/**
 * The tournament's own zone. An appointment sign-off is read on the venue's clock, not on the
 * reader's laptop, and never as the raw instant the API sends.
 *
 * **RUN 83 — re-exported, not redeclared.** This file and `hearings/documentText.ts` each used
 * to carry their own copy of the string, which is two constants that can drift and no single
 * place to look. It now comes from `app/tournamentTime.ts`, which is where the readings that use
 * it live, and every existing import of this name keeps working unchanged.
 */
export { TOURNAMENT_TIMEZONE };

/**
 * `18 Aug 11:03` — a sign-off instant, short enough for a chip on a fixture row.
 *
 * F-19: every slot used to print the whole ISO instant, three times per fixture, which made
 * the list unreadable. The chip says it once, in words a TD reads at a glance; the exact
 * instant stays on the chip's `title` and on its `data-confirmed-at`, so nothing is lost —
 * neither to a reader who needs the second, nor to a test.
 *
 * An unparseable instant is returned as it came. Inventing a date the API did not send would
 * be worse than showing the reader what it did.
 */
export function shortTournamentTime(at: string): string {
  const parsed = new Date(at);
  if (Number.isNaN(parsed.getTime())) return at;
  const day = new Intl.DateTimeFormat("en-GB", {
    timeZone: TOURNAMENT_TIMEZONE,
    day: "numeric",
    month: "short",
  }).format(parsed);
  return `${day} ${clockOf(parsed)}`;
}

/**
 * RUN 83B - `20 Sep 2026, 03:00:00 Tokyo time`: the WHOLE instant, for the hover text of the
 * sign-off and lock chips.
 *
 * F-19 put "the exact instant the API sent" on each chip's `title` so that nothing needing the
 * second was lost - and it put it there raw: `Signed off 2026-09-19T18:00:00Z`. That is the
 * right instant on the wrong clock. A TD hovering over a chip that reads `Confirmed 20 Sep 03:00`
 * was shown a moment on the 19th, nine hours earlier, with nothing but a trailing `Z` to say
 * why. The hover now gives the same instant to the second on the clock the chip itself uses,
 * and says which clock that is. The raw value is still on the element, as `data-confirmed-at`
 * / `data-locked-at`, for anything that needs the wire's own words.
 *
 * An unparseable instant is returned as it came, as {@link shortTournamentTime} does.
 */
export function exactTournamentInstant(at: string): string {
  const parsed = new Date(at);
  if (Number.isNaN(parsed.getTime())) return at;
  const second = String(tournamentClockParts(parsed).second).padStart(2, "0");
  return `${tournamentDateLong(parsed)}, ${tournamentTimeOfDay(parsed)}:${second} Tokyo time`;
}

/**
 * `11:03` on the tournament's clock — the one time formatter both chips share.
 *
 * RUN 83 points it at the shared reader rather than building a third formatter for the shape.
 * The digits are identical: `hour12: false` and `hourCycle: "h23"` both render a Tokyo midnight
 * as `00:00` on this runtime, which is the only hour the two spellings can disagree about.
 */
function clockOf(parsed: Date): string {
  return tournamentTimeOfDay(parsed);
}

/**
 * RUN 82 — `09:10`, the time half of {@link shortTournamentTime} and nothing else, for the
 * `On TMS · Locked 09:10` chip (§A11).
 *
 * The same zone and the same 24-hour digits as the sign-off chip — it is that function's own
 * formatter, not a second one that could drift from it — with the date left off: the chip sits
 * beside `Confirmed 18 Sep 08:55` in a status column F-21 already had to squeeze two chips into,
 * and a second date there would widen every row of the list to say what the chip beside it
 * already says. The whole instant stays on the chip's `title` and its `data-locked-at`, exactly
 * as the sign-off's does, and an unparseable instant is returned as it came, for the same
 * reason.
 */
export function tournamentClock(at: string): string {
  const parsed = new Date(at);
  if (Number.isNaN(parsed.getTime())) return at;
  return clockOf(parsed);
}

/**
 * How a fixture's sign-off and lock read **once**, on the fixture's own row (F-19).
 *
 * The five states the API can put a fixture in collapse to three questions the TD asks:
 * is it signed off, is it signed off *everywhere*, and is it locked. A fixture whose filled
 * slots are all confirmed says so once at the top. A fixture where the slots disagree — two
 * confirmed, one still open, which `POST /appointments/confirm` produces whenever a slot was
 * empty when the day was signed off — says nothing at the top, and the confirmed slots carry
 * their own chip instead. That is the "only where it differs" rule, and it is why this is one
 * function rather than a flag on each row.
 *
 * **RUN 82 adds the lock that is a record (§A11).** A fixture whose three slots are locked has
 * been marked as what TMS holds, and that outranks the sign-off it stands on: its chip stops
 * saying `Signed Off` and says `On TMS`, with the time it was locked. An Official match still
 * says `Official in TMS` whether or not it was locked first, because that is the lock nothing
 * reopens. And a fixture reopened for a correction carries `needsPush` whatever else it is —
 * signed off again or not — until the day is locked again.
 *
 * RUN 82A — the sign-off's chip read `Locked · Confirmed` until the lock layer gave `Locked` a
 * meaning of its own, and the Official one `Locked · Official in TMS`. A signed-off match that
 * says `Locked` beside a `Mark Day Locked` button that is still live is two claims about one word,
 * so the chips now say `Signed Off` and `Official in TMS`, and `Locked` means only the record.
 */
export interface FixtureSignOff {
  /** Every filled slot is signed off, so the fixture can state it once. */
  uniform: boolean;
  /** The earliest sign-off instant across the fixture's slots, or null where there is none. */
  at: string | null;
  /** The lock the whole fixture carries, in the words §A4 rule 6, §A11 and CC-Q25 use. */
  lock: "official" | "on_tms" | "confirmed" | null;
  /** RUN 82 — the earliest lock instant across the slots of a locked fixture, else null. */
  lockedAt: string | null;
  /** RUN 82 — reopened for a correction that TMS does not hold yet (§A11). */
  needsPush: boolean;
}

/** The earliest of some ISO instants, or null for none — the API sends them in one shape. */
function earliestOf(instants: readonly (string | null)[]): string | null {
  const present = instants.filter((instant): instant is string => instant !== null);
  if (present.length === 0) return null;
  return present.reduce((earliest, entry) => (entry < earliest ? entry : earliest));
}

export function fixtureSignOff(fixture: AppointmentFixtureBody): FixtureSignOff {
  const filled = fixture.slots.filter((slot) => slot.mediaName !== null);
  const confirmed = filled.filter((slot) => slot.confirmedAt !== null);
  const at = earliestOf(confirmed.map((slot) => slot.confirmedAt));
  const uniform = filled.length > 0 && confirmed.length === filled.length;
  return {
    uniform,
    at: uniform ? at : null,
    lock: fixture.frozen ? "official" : fixture.locked ? "on_tms" : uniform ? "confirmed" : null,
    lockedAt: fixture.locked ? earliestOf(fixture.slots.map((slot) => slot.lockedAt)) : null,
    needsPush: fixture.needsPush,
  };
}

/** A slot the engine chose, still untouched by hand. §A8 keeps hand edits; these it replaces. */
export function isAiDrafted(slot: AppointmentSlotBody): boolean {
  return slot.origin === "ai_draft" && slot.status === "ai_drafted";
}

/** Locked against a redraft — a hand edit or a sign-off (§A8, CC-Q25). */
export function isLocked(slot: AppointmentSlotBody): boolean {
  return slot.confirmedAt !== null || slot.origin === "td_manual";
}

/**
 * §F — the soft anomaly. TMS is the record and nothing is blocked, so this is drawn as an
 * amber Command Desk-style note and never as an error.
 */
export function isAnomaly(warning: AppointmentWarning): boolean {
  return warning.code === "fixture_already_official";
}

export function warningClass(warning: AppointmentWarning): string {
  if (isAnomaly(warning)) return "warnnote warnnote--anomaly";
  switch (warning.severity) {
    case "hard":
      return "warnnote warnnote--hard";
    case "attention":
      return "warnnote warnnote--attention";
    default:
      return "warnnote warnnote--advisory";
  }
}

/** Only a hard breach is an error; attention and advisory are read out politely. */
export function warningRole(warning: AppointmentWarning): "alert" | "status" {
  return warning.severity === "hard" && !isAnomaly(warning) ? "alert" : "status";
}

export function warningKind(warning: AppointmentWarning): string {
  if (isAnomaly(warning)) return "Anomaly Note";
  switch (warning.severity) {
    case "hard":
      return "Rule Breach";
    case "attention":
      return "Needs Attention";
    default:
      return "Advisory";
  }
}

/** The warnings that name one fixture. Day-wide warnings carry no match id and stay above. */
export function warningsForFixture(
  warnings: readonly AppointmentWarning[],
  tmsMatchDbId: number,
): AppointmentWarning[] {
  return warnings.filter((warning) => warning.tms_match_db_id === tmsMatchDbId);
}

/**
 * `3 days`, `1 match`, `60 matches` — a count and its noun, pluralised without embarrassing
 * the Technical Delegate.
 *
 * English adds `es` rather than `s` after a sibilant, and the one that keeps arriving in this
 * vocabulary is `match` — a stage draft reporting **60 matchs** was found on the screen and
 * had to be fixed after the fact. So the rule lives in ONE function that every coverage
 * sentence on both appointment pages calls, rather than in an `es` argument each of them has
 * to remember to pass. A guard you can forget at a call site is a guard that will be forgotten
 * at a call site.
 *
 * Deliberately narrow: it handles the regular `s`, the sibilant `es` (`match`, `breach`,
 * `class`, `box`), and nothing else. An irregular plural should be written out rather than
 * guessed at by a helper on an appointments page.
 */
export function countOf(count: number, noun: string): string {
  if (count === 1) return `${String(count)} ${noun}`;
  const sibilant = /(?:ch|sh|s|x|z)$/.test(noun);
  return `${String(count)} ${noun}${sibilant ? "es" : "s"}`;
}

export function daySummaryCounts(warnings: readonly AppointmentWarning[]): {
  hard: number;
  attention: number;
  advisory: number;
  anomaly: number;
} {
  let hard = 0;
  let attention = 0;
  let advisory = 0;
  let anomaly = 0;
  for (const warning of warnings) {
    if (isAnomaly(warning)) {
      anomaly += 1;
      continue;
    }
    if (warning.severity === "hard") hard += 1;
    else if (warning.severity === "attention") attention += 1;
    else advisory += 1;
  }
  return { hard, attention, advisory, anomaly };
}

/**
 * Who the picker offers for a slot — the same set the API's `isRoleEligible` accepts, and no
 * wider, because a name offered here that the server refuses is a control that lies.
 *
 * AG2026's "old method" (§A1): Technical Officers take TO, Judges take SJ and TJ, and that is
 * how the engine drafts. But §A1 says who *usually* does what, not who may, and on 26 September
 * 2026 the Technical Delegate asked to be able to seat a Judge in the TO chair by hand. So the
 * TO slot now offers Judges as well — under their own heading, below the ATDs — while SJ and TJ
 * are unchanged: a Technical Officer is still not offered a judge's seat, because nobody asked.
 */
export function eligibleForSlot(role: string, slot: TechnicalSlot): boolean {
  if (slot === "TO") return role === "TO" || role === "Judge" || role === "ATD";
  return role === "Judge" || role === "ATD";
}

/** Heaviest first, so the imbalance the TD is looking for is at the top. */
export function sortWorkloads(workloads: readonly OfficialWorkload[]): OfficialWorkload[] {
  return [...workloads].sort(
    (a, b) =>
      b.total - a.total ||
      b.sj_tj_difference - a.sj_tj_difference ||
      (a.official_id < b.official_id ? -1 : a.official_id > b.official_id ? 1 : 0),
  );
}

/* ── The slot picker's options (§A6) ──────────────────────────────────────── */

/**
 * §A4's two documented defaults, mirrored on this side for the picker's **advice only**.
 *
 * The tournament's real rule set lives in Configuration §06 and is applied by the server on
 * every write; nothing here decides anything. These two numbers exist so that the picker can
 * say "not recommended" beside a name before the TD picks it, which is the whole difference
 * between a dropdown and the mockup's popover. If the tournament raises its tolerance the
 * server will accept an appointment this list gently discouraged — which is the right way
 * round, and the reason the advisory never blocks.
 *
 * They are marked here rather than hidden in a component so that wiring them to the
 * configuration endpoint later is one change in one place.
 */
export const ADVISORY_DAILY_CAP = 2;
export const ADVISORY_SJ_TJ_TOLERANCE = 2;

/**
 * Why an option is marked, in the order the picker applies the tests.
 *
 * `blocked` is the API's answer, not a preference: a blocked option is one `POST` or `PATCH`
 * would refuse outright and which has no override at all (§A4 rule 1, and nobody can be at
 * two matches at once). Drawing it as choosable would be drawing a control the server
 * refuses.
 *
 * `own_country` is deliberately NOT blocked. §A7 gives the country rule an explicit two-step
 * override, so the option is offered struck through and greyed — the mockup's own treatment —
 * and choosing it walks into the override modal exactly as the spec requires. Making it
 * `disabled`, as the design draft did, would remove the override from the only place the TD
 * would look for it.
 */
export type PickerFlagKind = "in_this_match" | "same_kickoff" | "own_country" | "not_recommended";

export interface PickerFlag {
  kind: PickerFlagKind;
  /** The chip's words — upper-cased by the stylesheet, not here. */
  label: string;
  /** True where the API would refuse the pick outright. */
  blocked: boolean;
  /** One sentence, for the option's `title` and for the advisory line after a pick. */
  detail: string;
}

export interface PickerOption {
  official: OfficialView;
  flag: PickerFlag | null;
  /** ATDs are pinned below a separator and are never proposed by the engine (§A4 rule 3). */
  isAtd: boolean;
  /**
   * A Judge offered for the TO chair — listed in a group of its own, BELOW the ATDs, which is
   * where the Technical Delegate asked for them (26 Sep 2026).
   *
   * Deliberately NOT flagged "Not Recommended", unlike an ATD. The ATD flag stands on a
   * written rule — §A4 rule 3, "selecting one shows a warning" — and there is no such rule for
   * a Judge: §A1 says Judges "usually" take SJ and TJ, which is a description of the draft,
   * not a caution against the pick. A Judge in this group therefore carries only the flags
   * anybody else would — clash, own country, the daily-load advice — and nothing for being a
   * Judge. The separate heading is what says "not your usual TO"; the engine, which still
   * never drafts a Judge into TO, is what keeps it unusual.
   *
   * Always false on an SJ or TJ slot, where a Judge is the ordinary choice and sits in the
   * main list.
   */
  isJudgeForTo: boolean;
}

/**
 * Everybody the TD may put in this slot, each carrying the one thing worth saying about them.
 *
 * The tests run in the order the dropdown makes them (§A4): the impossible first, then the
 * hard-line country rule, then the soft advice. Only one flag is shown, because a name with
 * three chips beside it is a name nobody reads.
 */
export function pickerOptions(
  fixture: AppointmentFixtureBody,
  slot: TechnicalSlot,
  officials: readonly OfficialView[],
  /** Every fixture in the fetched scope — the clash and workload tests need the whole day. */
  dayFixtures: readonly AppointmentFixtureBody[],
): PickerOption[] {
  const inThisMatch = new Set(
    fixture.slots
      .filter((entry) => entry.slot !== slot && entry.tmsPersonId !== null)
      .map((entry) => entry.tmsPersonId as string),
  );

  const atSameKickOff = new Set<string>();
  const heldToday = new Map<string, number>();
  const judgeCounts = new Map<string, { sj: number; tj: number }>();

  for (const other of dayFixtures) {
    for (const entry of other.slots) {
      if (entry.tmsPersonId === null) continue;
      heldToday.set(entry.tmsPersonId, (heldToday.get(entry.tmsPersonId) ?? 0) + 1);
      if (entry.slot === "SJ" || entry.slot === "TJ") {
        const counts = judgeCounts.get(entry.tmsPersonId) ?? { sj: 0, tj: 0 };
        if (entry.slot === "SJ") counts.sj += 1;
        else counts.tj += 1;
        judgeCounts.set(entry.tmsPersonId, counts);
      }
      if (
        other.tmsMatchDbId !== fixture.tmsMatchDbId &&
        other.date === fixture.date &&
        other.time === fixture.time
      ) {
        atSameKickOff.add(entry.tmsPersonId);
      }
    }
  }

  const current = fixture.slots.find((entry) => entry.slot === slot)?.tmsPersonId ?? null;

  return officials
    .filter((official) => eligibleForSlot(official.role, slot))
    .map((official) => {
      const id = official.tmsPersonId;
      const isAtd = official.role === "ATD";
      const isJudgeForTo = slot === "TO" && official.role === "Judge";
      return { official, isAtd, isJudgeForTo, flag: flagFor() };

      function flagFor(): PickerFlag | null {
        if (inThisMatch.has(id)) {
          return {
            kind: "in_this_match",
            label: "Already In This Match",
            blocked: true,
            detail:
              "This official already holds another slot in this match. One official, one " +
              "role per match, and that rule has no override.",
          };
        }
        if (atSameKickOff.has(id)) {
          return {
            kind: "same_kickoff",
            label: "Already At This Kick-Off",
            blocked: true,
            detail:
              "This official is already appointed to another match starting at the same " +
              "time. Nobody can be at two matches at once.",
          };
        }
        if (
          official.country === fixture.homeTeamCode ||
          official.country === fixture.awayTeamCode
        ) {
          return {
            kind: "own_country",
            label: "Own Country",
            blocked: false,
            detail:
              `${official.country} is playing in this match. This is a hard-line rule — ` +
              "choosing this official asks you to confirm an override, and the slot stays " +
              "flagged afterwards.",
          };
        }
        if (isAtd) {
          return {
            kind: "not_recommended",
            label: "Not Recommended",
            blocked: false,
            detail:
              "An Assistant Technical Delegate may sit at the table but is never proposed " +
              "by the drafting engine.",
          };
        }
        // Workload advice, from here down. The slot's current holder is excluded from the
        // count of what a pick would ADD, because re-picking him changes nothing.
        const already = (heldToday.get(id) ?? 0) - (id === current ? 1 : 0);
        if (already >= ADVISORY_DAILY_CAP) {
          return {
            kind: "not_recommended",
            label: "Not Recommended",
            blocked: false,
            detail:
              `This official already holds ${String(already)} appointments on this day, ` +
              `which is the usual daily limit of ${String(ADVISORY_DAILY_CAP)}.`,
          };
        }
        if (slot === "SJ" || slot === "TJ") {
          const counts = judgeCounts.get(id) ?? { sj: 0, tj: 0 };
          const sj = counts.sj + (slot === "SJ" ? 1 : 0) - (id === current && slot === "SJ" ? 1 : 0);
          const tj = counts.tj + (slot === "TJ" ? 1 : 0) - (id === current && slot === "TJ" ? 1 : 0);
          if (Math.abs(sj - tj) > ADVISORY_SJ_TJ_TOLERANCE) {
            return {
              kind: "not_recommended",
              label: "Not Recommended",
              blocked: false,
              detail:
                `This would leave the judge on ${String(sj)} scoring and ${String(tj)} timing ` +
                `appointments — a spread beyond the tolerance of ` +
                `${String(ADVISORY_SJ_TJ_TOLERANCE)}.`,
            };
          }
        }
        return null;
      }
    })
    .sort((a, b) => {
      // The usual holders first; then the ATDs under their own separator (§A4 rule 3); then,
      // on a TO slot only, the Judges under theirs — below the ATDs, as the TD asked. Within
      // each group, alphabetically by the name as it is printed, so the list reads the way the
      // roster does.
      const group = (option: PickerOption): number =>
        option.isJudgeForTo ? 2 : option.isAtd ? 1 : 0;
      if (group(a) !== group(b)) return group(a) - group(b);
      return a.official.mediaName < b.official.mediaName
        ? -1
        : a.official.mediaName > b.official.mediaName
          ? 1
          : 0;
    });
}

/** The avatar's initials — first and last of the printed name, as the mockup composes them. */
export function initialsOf(mediaName: string): string {
  const parts = mediaName.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";
  return `${first}${last}`.toUpperCase();
}
